import type { RouteViewModelCorrectionOperation } from '../../../types/routeViewModel';
import type { CorrectionContext } from '../hooks/useCorrectionContext';

/**
 * Phase 1 top-level correction scenarios (BS §2.2, TP P1-1 / GAP-P1-01).
 * Backend operations such as `CARRIER_REPLACEMENT` are nested under `EARLY_COMPLETION`, not a fifth scenario.
 */
export type CorrectionScenarioId =
  | 'RESOURCE_CHANGE'
  | 'STOP_CHANGE'
  | 'ADD_CARGO'
  | 'EARLY_COMPLETION';

/** Normative count — must stay 4 until Business Standard changes. */
export const PHASE1_TOP_LEVEL_SCENARIO_COUNT = 4;

/** Dropdown / shell labels aligned with Business Standard §2.2. */
export const CORRECTION_SCENARIO_DROPDOWN_LABELS: Record<CorrectionScenarioId, string> = {
  RESOURCE_CHANGE: 'Замена водителя / автомобиля / прицепа',
  STOP_CHANGE: 'Изменение точек маршрута / связанного груза',
  ADD_CARGO: 'Догруз',
  EARLY_COMPLETION: 'Досрочное завершение'
};

/** Nested sub-flow under scenario 4 (BS-S1) — not a top-level scenario id. */
export const EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE = 'Смена перевозчика';

export type CorrectionScenarioDefinition = {
  id: CorrectionScenarioId;
  title: string;
  description: string;
  /** At least one of these operations must be allowed for the scenario to be selectable. */
  requiredAnyOperations: RouteViewModelCorrectionOperation[];
};

export const CORRECTION_SCENARIO_HUB_QUESTION =
  'Что произошло / что нужно сделать?';

export const CORRECTION_SCENARIOS: CorrectionScenarioDefinition[] = [
  {
    id: 'RESOURCE_CHANGE',
    title: CORRECTION_SCENARIO_DROPDOWN_LABELS.RESOURCE_CHANGE,
    description: 'Смена водителя, тягача или прицепа без изменения маршрута и груза.',
    requiredAnyOperations: ['RESOURCE_DRIVER', 'RESOURCE_VEHICLE', 'RESOURCE_TRAILER']
  },
  {
    id: 'STOP_CHANGE',
    title: CORRECTION_SCENARIO_DROPDOWN_LABELS.STOP_CHANGE,
    description:
      'Изменение будущих точек (в т.ч. более ранняя выгрузка по просьбе клиента), дополнительная выгрузка и распределение. Новая заявка на маршрут — сценарий «Догруз».',
    requiredAnyOperations: ['STOP_FUTURE_EDIT', 'ADD_UNLOAD']
  },
  {
    id: 'ADD_CARGO',
    title: CORRECTION_SCENARIO_DROPDOWN_LABELS.ADD_CARGO,
    description:
      'Догруз новой заявки на маршрут: источник погрузки (заявка / склад), новая погрузка и выгрузка.',
    requiredAnyOperations: ['ADD_CARGO_SEGMENT']
  },
  {
    id: 'EARLY_COMPLETION',
    title: CORRECTION_SCENARIO_DROPDOWN_LABELS.EARLY_COMPLETION,
    description: `Под-сценарий «${EARLY_COMPLETION_CARRIER_SUBFLOW_TITLE}»: завершение текущего маршрута, складской handoff и ручное создание нового маршрута (без автосоздания).`,
    requiredAnyOperations: ['EARLY_COMPLETION', 'CARRIER_REPLACEMENT']
  }
];

/** Ensures registry invariants (tests / dev). */
export function assertPhase1ScenarioRegistry(): void {
  if (CORRECTION_SCENARIOS.length !== PHASE1_TOP_LEVEL_SCENARIO_COUNT) {
    throw new Error(
      `Expected ${PHASE1_TOP_LEVEL_SCENARIO_COUNT} top-level scenarios, got ${CORRECTION_SCENARIOS.length}`
    );
  }
  const ids = new Set(CORRECTION_SCENARIOS.map((s) => s.id));
  if (ids.size !== PHASE1_TOP_LEVEL_SCENARIO_COUNT) {
    throw new Error('Duplicate CorrectionScenarioId in CORRECTION_SCENARIOS');
  }
}

export function isScenarioOperationAllowed(
  ctx: CorrectionContext,
  scenario: CorrectionScenarioDefinition
): boolean {
  return scenario.requiredAnyOperations.some((op) => ctx.isOperationAllowed(op));
}

export function getScenarioBlockedReason(
  ctx: CorrectionContext,
  scenario: CorrectionScenarioDefinition
): string | null {
  if (isScenarioOperationAllowed(ctx, scenario)) return null;

  if (scenario.id === 'STOP_CHANGE') {
    return 'Изменение точек недоступно: нет редактируемых будущих остановок и добавление выгрузки запрещено.';
  }

  if (scenario.id === 'ADD_CARGO') {
    return 'Догруз недоступен для текущего статуса маршрута.';
  }

  if (scenario.id === 'EARLY_COMPLETION') {
    if (!ctx.isOperationAllowed('EARLY_COMPLETION') && !ctx.isOperationAllowed('CARRIER_REPLACEMENT')) {
      return 'Досрочное завершение недоступно для текущего статуса.';
    }
    return 'Досрочное завершение недоступно для текущего статуса.';
  }

  if (scenario.id === 'RESOURCE_CHANGE') {
    return 'Смена ресурсов недоступна для текущего статуса маршрута.';
  }

  return 'Сценарий недоступен для текущего статуса маршрута.';
}

/** Maps backend correction operations to primary scenario (for tests / tooling). */
export function scenarioIdsForOperation(
  op: RouteViewModelCorrectionOperation
): CorrectionScenarioId[] {
  const map: Partial<Record<RouteViewModelCorrectionOperation, CorrectionScenarioId[]>> = {
    RESOURCE_DRIVER: ['RESOURCE_CHANGE'],
    RESOURCE_VEHICLE: ['RESOURCE_CHANGE'],
    RESOURCE_TRAILER: ['RESOURCE_CHANGE'],
    CARRIER_REPLACEMENT: ['EARLY_COMPLETION'],
    STOP_FUTURE_EDIT: ['STOP_CHANGE'],
    ADD_UNLOAD: ['STOP_CHANGE'],
    ADD_CARGO_SEGMENT: ['ADD_CARGO', 'STOP_CHANGE'],
    EARLY_COMPLETION: ['EARLY_COMPLETION']
  };
  return map[op] ?? [];
}
